import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import styled from 'styled-components';
import Link from 'next/link';

import { Caret } from 'shared/icons/Caret';
import { HeaderNavigationName } from 'layout/header/components/HeaderNavigationName';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { trackHeaderBreadcrumbClick } from 'layout/header/utils/trackHeaderBreadcrumbClick';
import { trackHeaderNavClick } from 'layout/header/utils/trackHeaderNavClick';
import { variableFontRegular } from 'styles/theme/text';

interface ISubMenuNavigation {
  menuItems?: Array<ICMSBreadcrumb>;
  originalLabel: string;
  label: string;
  href: string;
  specialNavLabel?: string | null;
}

export const SubMenuNavigation = ({
  menuItems = [],
  originalLabel,
  label,
  href,
  specialNavLabel = null,
}: ISubMenuNavigation) => {
  const handleTitleClick = () => {
    if (originalLabel === 'Timeline') {
      sendTrackingEvent('timeline_tap');
      trackHeaderNavClick('timeline');
    } else {
      sendTrackingEvent('shop_listings_tap');
      trackHeaderNavClick('shop');
    }
  };

  const handleBreadcrumbClick = (title, name, slug) => {
    if (originalLabel === 'Timeline') {
      const productTemplateSlug = slug?.split('/')?.[1];
      trackHeaderBreadcrumbClick('Calendar', title, name, productTemplateSlug || slug);
    } else if (specialNavLabel) {
      trackHeaderBreadcrumbClick(specialNavLabel, title, name, slug);
    } else {
      trackHeaderBreadcrumbClick(label, title, name, slug);
    }
  };

  return (
    <SubMenuNavigation.Container data-qa="submenuNavigation_container">
      <div className="titleAndArrowContainer">
        <Link
          href={href}
          passHref
          prefetch={false}
          tabIndex={0}
          role="link"
          data-qa="header_link"
          onClick={handleTitleClick}
          onKeyDown={handleTitleClick}
        >
          {label}
        </Link>
        <div className="caretWrapper">
          <Caret color="white" direction="right" />
        </div>
      </div>

      <div className="flexWrapper">
        {menuItems?.map(({ name, children = [] }) => (
          <SubMenuNavigation.MenuColumn key={name} columnCount={menuItems.length}>
            <h4>{name}</h4>
            <div className="list">
              {children.map(({ name: childName, slug }: { name: string; slug: string }) => {
                return (
                  <SubMenuNavigation.Link key={slug} $isTimeline={originalLabel === 'Timeline'}>
                    <Link
                      href={`/${slug}`}
                      passHref
                      prefetch={false}
                      tabIndex={0}
                      role="link"
                      data-qa="menu_dropdown_link"
                      onClick={() => handleBreadcrumbClick(name, childName, slug)}
                      onKeyDown={() => handleBreadcrumbClick(name, childName, slug)}
                      aria-label="menu dropdown link"
                    >
                      <HeaderNavigationName name={childName} />
                    </Link>
                  </SubMenuNavigation.Link>
                );
              })}
            </div>
          </SubMenuNavigation.MenuColumn>
        ))}
      </div>
    </SubMenuNavigation.Container>
  );
};

SubMenuNavigation.Container = styled.div`
  padding-top: 6px;
  .titleAndArrowContainer {
    display: flex;
    align-items: center;

    ${variableFontRegular({ height: '12px' })};

    &:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
      outline: none;
    }

    a {
      ${variableFontRegular({ height: '12px' })};
    }
  }

  .caretWrapper {
    margin-left: 6px;
    margin-bottom: 2px;
  }

  .flexWrapper {
    display: flex;
    flex-direction: column;
    margin-top: 32px;
  }
`;

SubMenuNavigation.MenuColumn = styled.div<{ columnCount?: number }>`
  margin-bottom: 16px;
  margin-right: 32px;
  min-width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};
  width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};

  &:last-child {
    margin-right: 0;
  }

  > h4 {
    margin: 0 0 24px 0;
    cursor: default;
    font-weight: 400;
    ${variableFontRegular({ height: '12px' })};
    color: ${({ theme }) => theme.colors.darkGray5};
  }

  .list {
    min-width: calc(100vw - 64px);
    display: flex;
    flex-wrap: wrap;
  }
`;

SubMenuNavigation.Link = styled.div<{ $isTimeline: boolean }>`
  ${variableFontRegular({ height: '12px' })};
  width: ${({ $isTimeline }) => ($isTimeline ? `100%` : `50%`)};

  > a {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 245px;
    padding-bottom: 16px;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 2px;
      outline: none;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;
